import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { usePageContents } from '../contexts/PageContentContext';

const Greeting: React.FC = () => {
  const { getPageContent, loading } = usePageContents();
  const content = getPageContent('greeting');

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex items-center justify-center min-h-[250px] sm:min-h-[300px] md:min-h-[400px] mt-20">
          <div className="text-sm sm:text-base text-gray-500">로딩 중...</div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Page Header with Image */}
      <div className="relative text-white py-12 sm:py-16 md:py-20 lg:py-24 mt-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1920&h=600&fit=crop&q=80"
            alt="Library"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <p className="text-gray-200 mb-2 sm:mb-3 text-xs sm:text-sm font-medium tracking-wider">센터소개</p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">인사말</h1>
          <div className="w-16 sm:w-20 h-1 bg-white/70"></div>
        </div>
      </div>

      {/* Welcome Section - White Background */}
      <div className="flex-1 py-10 sm:py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6 sm:mb-8" style={{ lineHeight: '1.4' }}>
              {content?.title_main || '동두천시 글로벌인재교육센터를'}<br />
              {content?.title_sub || '방문해주신 여러분을'}<br />
              <span className="text-green-500">{content?.title_eng || '진심으로 환영합니다.'}</span>
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-green-500 mx-auto"></div>
          </div>
        </div>
      </div>

      {/* About Section - Light Gray Background */}
      <div className="py-10 sm:py-16 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            {content?.description ? (
              <div
                className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed text-center mb-6 sm:mb-8 prose prose-base sm:prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: content.description }}
              />
            ) : (
              <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed text-center mb-6 sm:mb-8">
                동두천시 글로벌인재교육센터는 <span className="font-bold text-gray-900">동두천시가 설립하고 ㈜한원글로벌이 위탁 운영하는</span> 지역 대표 글로벌 교육기관으로, 미래세대가 세계와 소통하며 성장할 수 있도록 창의적이고 실용적인 글로벌 교육 프로그램을 운영하고 있습니다.
              </p>
            )}

            {/* 이미지가 있으면 표시 */}
            {content?.image_url && (
              <div className="mt-8 sm:mt-10 md:mt-12">
                <img
                  src={content.image_url}
                  alt="인사말 이미지"
                  className="w-full max-w-4xl mx-auto rounded-xl sm:rounded-2xl shadow-lg"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Core Values - White Background with Cards */}
      <div className="py-10 sm:py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">
              <span className="font-light text-gray-600">동두천시 글로벌인재교육센터는</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Card 01 */}
            <div className="group">
              <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-500">01</span>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight">
                  실생활 중심의 언어교육,<br />
                  STEAM·AI 기반 융합체험
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  학생들이 스스로 배우고 성장하는 '<span className="font-semibold text-green-600">살아있는 국제교육의 장</span>'을 만들어가고 있습니다.
                </p>
                <div className="mt-4 sm:mt-6 text-3xl sm:text-4xl opacity-20 group-hover:opacity-40 transition-opacity">
                  🎓
                </div>
              </div>
            </div>

            {/* Card 02 */}
            <div className="group">
              <div className="bg-gradient-to-br from-green-50 to-white border border-gray-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-green-600">02</span>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight">
                  다문화 학생과 일반 학생이<br />
                  함께 배우는 포용적 환경
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  모든 아이가 자신의 <span className="font-semibold text-green-600">문화적 정체성을 존중받으며</span> 세계시민으로 성장할 수 있도록 돕고 있습니다.
                </p>
                <div className="mt-4 sm:mt-6 text-3xl sm:text-4xl opacity-20 group-hover:opacity-40 transition-opacity">
                  🤝
                </div>
              </div>
            </div>

            {/* Card 03 */}
            <div className="group">
              <div className="bg-gradient-to-br from-teal-50 to-white border border-gray-200 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 h-full hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-start gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-teal-600">03</span>
                </div>
                <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-3 sm:mb-4 leading-tight">
                  동두천을<br />
                  "글로벌 교육도시"로
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  ㈜한원글로벌은 위탁운영기관으로서 <span className="font-semibold text-teal-600">지역의 교육경쟁력을 높이고</span> 동두천이 글로벌 교육도시로 도약할 수 있도록 최선을 다하겠습니다.
                </p>
                <div className="mt-4 sm:mt-6 text-3xl sm:text-4xl opacity-20 group-hover:opacity-40 transition-opacity">
                  🌍
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vision Section - Green Background with Image */}
      <div className="py-10 sm:py-16 md:py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=1920&h=600&fit=crop&q=80"
            alt="Students"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-700/90 to-teal-800/90"></div>
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3 bg-white/10 backdrop-blur-sm px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-full">
                <span className="text-xl sm:text-2xl">✨</span>
                <span className="font-semibold text-sm sm:text-base md:text-lg">Our Vision</span>
              </div>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 leading-relaxed">
              미래세대가 세계와 소통하며<br />
              성장할 수 있도록
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-green-100 max-w-3xl mx-auto leading-relaxed">
              창의적이고 실용적인 글로벌 교육 프로그램을 통해<br />
              차세대 글로벌 인재를 양성합니다
            </p>
          </div>
        </div>
      </div>

      {/* Closing Section - White Background */}
      <div className="py-10 sm:py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-6 sm:mb-8">감사합니다.</p>
            <div className="inline-block border-t-2 border-gray-300 pt-6 sm:pt-8">
              <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                동두천시 글로벌인재교육센터 직원일동
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Greeting;
